(() => {
  const modal = document.querySelector('[data-quick-add-modal]');
  if (!modal || modal.dataset.quickAddReady === 'true') return;
  modal.dataset.quickAddReady = 'true';

  const content = modal.querySelector('[data-quick-add-content]');
  const productCache = new Map();
  let activeProduct = null;
  let selectedOptions = [];
  let trigger = null;

  const escapeHTML = (value = '') =>
    value
      .toString()
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const format = window.moneyFormat || '${{amount}}';

    return format
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', Math.round(Number(amount)).toString())
      .replace('{{ amount_no_decimals }}', Math.round(Number(amount)).toString());
  };

  const imageUrl = (image) => {
    const source = typeof image === 'string' ? image : image?.src;
    if (!source) return '';
    return source.startsWith('//') ? `https:${source}` : source;
  };

  const colorValue = (value = '') => {
    const colors = {
      'army green': '#596f47',
      'dark gray': '#555555',
      'dark grey': '#555555',
      'light blue': '#91b7d7',
      'wine red': '#742433',
      burgundy: '#742433',
      navy: '#182848',
      black: '#111111',
      white: '#f7f7f5',
      cream: '#eee4d2',
      beige: '#d8c4a5',
      pink: '#dfa0b7',
      yellow: '#efd13d',
      red: '#bd2f3a',
      blue: '#335f8d',
      green: '#55754b',
      gray: '#8a8a8a',
      grey: '#8a8a8a'
    };

    return colors[value.toLowerCase()] || value.toLowerCase().replaceAll(' ', '');
  };

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error('Unable to load product');
      return response.json();
    });

    productCache.set(handle, request);
    return request;
  };

  const findVariant = () =>
    activeProduct?.variants?.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );

  const optionAvailable = (optionIndex, value) =>
    activeProduct.variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== value) return false;

      return variant.options.every(
        (option, index) => index >= optionIndex || option === selectedOptions[index]
      );
    });

  const optionImage = (optionIndex, value) => {
    const withImage = activeProduct.variants.find(
      (variant) => variant.options[optionIndex] === value && imageUrl(variant.featured_image)
    );
    return imageUrl(withImage?.featured_image);
  };

  const renderOption = (option, optionIndex) => {
    const isColor = /colou?r/i.test(option.name);
    const choices = option.values
      .map((value) => {
        const selected = selectedOptions[optionIndex] === value;
        const available = optionAvailable(optionIndex, value);
        const image = isColor ? optionImage(optionIndex, value) : '';
        const className = [
          'store-quick-add__choice',
          isColor ? 'store-quick-add__choice--color' : '',
          selected ? 'is-selected' : ''
        ]
          .filter(Boolean)
          .join(' ');

        return `
          <button
            class="${className}"
            type="button"
            data-quick-add-option="${optionIndex}"
            data-quick-add-value="${escapeHTML(value)}"
            aria-pressed="${selected}"
            aria-label="${escapeHTML(`${option.name}: ${value}`)}"
            ${available ? '' : 'disabled'}
            ${isColor && !image ? `style="--quick-add-swatch:${escapeHTML(colorValue(value))}"` : ''}
          >
            ${isColor && image
              ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(value)}" width="164" height="224" loading="lazy">`
              : isColor
                ? `<span class="visually-hidden">${escapeHTML(value)}</span>`
                : escapeHTML(value)}
          </button>
        `;
      })
      .join('');

    return `
      <fieldset class="store-quick-add__option">
        <legend>${escapeHTML(option.name)}: <span>${escapeHTML(selectedOptions[optionIndex])}</span></legend>
        <div class="store-quick-add__choices${isColor ? ' store-quick-add__choices--color' : ''}">
          ${choices}
        </div>
      </fieldset>
    `;
  };

  const render = () => {
    const variant = findVariant() || activeProduct.variants.find((candidate) => candidate.available);
    if (!variant) {
      content.innerHTML = '<div class="store-quick-add__error">This product is currently unavailable.</div>';
      return;
    }

    const image = imageUrl(variant.featured_image) || imageUrl(activeProduct.featured_image);
    const compareAt = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    const options = activeProduct.options
      .map((option, index) => {
        const isDefaultTitle =
          option.name === 'Title' &&
          option.values.length === 1 &&
          option.values[0] === 'Default Title';
        return isDefaultTitle ? '' : renderOption(option, index);
      })
      .join('');

    content.innerHTML = `
      <div class="store-quick-add__product">
        <div class="store-quick-add__media">
          ${image
            ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(activeProduct.title)}" width="240" height="360">`
            : ''}
        </div>
        <div>
          <h2 class="store-quick-add__title" id="StoreQuickAddTitle">${escapeHTML(activeProduct.title)}</h2>
          <div class="store-quick-add__price">
            ${compareAt > price ? `<s>${formatMoney(compareAt)}</s>` : ''}
            <span>${formatMoney(price)}</span>
          </div>
        </div>
      </div>
      <form class="store-quick-add__form" data-quick-add-form>
        ${options}
        <button
          class="store-quick-add__submit"
          type="submit"
          ${variant.available ? '' : 'disabled'}
        >
          ${variant.available ? 'ADD TO CART' : 'SOLD OUT'}
        </button>
      </form>
    `;
  };

  const open = async (handle, source) => {
    trigger = source;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('store-quick-add-open', 'is-locked');
    content.innerHTML = '<div class="store-quick-add__loading">Loading...</div>';
    modal.querySelector('[data-quick-add-close]:not(.store-quick-add__backdrop)')?.focus({
      preventScroll: true
    });

    try {
      activeProduct = await fetchProduct(handle);
      const firstVariant =
        activeProduct.variants.find((variant) => variant.available) || activeProduct.variants[0];
      selectedOptions = [...firstVariant.options];
      render();
    } catch (error) {
      content.innerHTML =
        '<div class="store-quick-add__error">Product options could not be loaded. Please try again.</div>';
    }
  };

  const close = () => {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('store-quick-add-open');
    if (!document.querySelector('.drawer.is-open')) {
      document.body.classList.remove('is-locked');
    }
    trigger?.focus?.({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-quick-add-open]');
    if (opener) {
      event.preventDefault();
      open(opener.dataset.quickAddOpen, opener);
      return;
    }

    if (event.target.closest('[data-quick-add-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const option = event.target.closest('[data-quick-add-option]');
    if (!option || !activeProduct) return;

    const optionIndex = Number(option.dataset.quickAddOption);
    const value = option.dataset.quickAddValue;
    selectedOptions[optionIndex] = value;

    let variant = findVariant();
    if (!variant?.available) {
      variant = activeProduct.variants.find(
        (candidate) => candidate.available && candidate.options[optionIndex] === value
      );
      if (variant) selectedOptions = [...variant.options];
    }

    render();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add-form]');
    if (!form || !activeProduct) return;
    event.preventDefault();

    const variant = findVariant();
    const submit = form.querySelector('[type="submit"]');
    if (!variant?.available || !submit) return;

    submit.disabled = true;
    submit.textContent = 'ADDING...';

    try {
      const response = await fetch(window.themeRoutes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variant.id), quantity: 1 }]
        })
      });
      if (!response.ok) throw new Error('Unable to add product');

      const cartResponse = await fetch(window.themeRoutes.cart, {
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh cart');

      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('store:cart-updated', { detail: { cart } }));
      close();
      document.querySelector('[data-drawer-open="cart"]')?.click();
    } catch (error) {
      submit.disabled = false;
      submit.textContent = 'TRY AGAIN';
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) close();
  });
})();
